import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export type RevealVariant =
  | "up"
  | "fade"
  | "wipe"
  | "wipeX"
  | "blur"
  | "scale"
  | "left"
  | "right";

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  variant?: RevealVariant;
  as?: "div" | "section" | "li" | "figure" | "span" | "header" | "article" | "p" | "h2" | "h3";
  threshold?: number;
  rootMargin?: string;
};

/**
 * Hidden/shown class pairs per variant. Both strings are written out in full so
 * Tailwind's scanner can see them; only one of the pair is applied at a time.
 */
const VARIANTS: Record<RevealVariant, { hidden: string; shown: string; ms: number }> = {
  up: {
    hidden: "translate-y-8 opacity-0",
    shown: "translate-y-0 opacity-100",
    ms: 1100,
  },
  fade: {
    hidden: "opacity-0",
    shown: "opacity-100",
    ms: 1200,
  },
  wipe: {
    hidden: "translate-y-6 opacity-0 [clip-path:inset(0_0_100%_0)]",
    shown: "translate-y-0 opacity-100 [clip-path:inset(0_0_0_0)]",
    ms: 1400,
  },
  wipeX: {
    hidden: "opacity-0 [clip-path:inset(0_100%_0_0)]",
    shown: "opacity-100 [clip-path:inset(0_0%_0_0)]",
    ms: 1300,
  },
  blur: {
    hidden: "scale-[1.04] opacity-0 blur-xl",
    shown: "scale-100 opacity-100 blur-none",
    ms: 1250,
  },
  scale: {
    hidden: "scale-95 opacity-0",
    shown: "scale-100 opacity-100",
    ms: 900,
  },
  left: {
    hidden: "-translate-x-10 opacity-0",
    shown: "translate-x-0 opacity-100",
    ms: 1000,
  },
  right: {
    hidden: "translate-x-10 opacity-0",
    shown: "translate-x-0 opacity-100",
    ms: 1000,
  },
};

export function Reveal({
  children,
  className,
  delay = 0,
  duration,
  variant = "up",
  as = "div",
  threshold = 0.12,
  rootMargin = "0px 0px -8% 0px",
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const Tag = as as "div";
  const v = VARIANTS[variant];

  return (
    <Tag
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn(
        "transition-[opacity,transform,filter,clip-path] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
        visible ? v.shown : v.hidden,
        className,
      )}
      style={{
        transitionDelay: `${delay}ms`,
        transitionDuration: `${duration ?? v.ms}ms`,
      }}
    >
      {children}
    </Tag>
  );
}
